import { useState } from 'react'
import { KeyRound } from 'lucide-react'
import {
  Alert,
  Button,
  CopyButton,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  FormattedDate,
} from '@ideon-api/ideon-ui'
import { useMachineUsers, type MachineUser } from '../../lib/use-service-accounts'
import { CreateMachineUserDialog } from './CreateMachineUserDialog'
import { RevokeMachineUserDialog } from './RevokeMachineUserDialog'

// Middle-truncated so a 32-char client id fits the column; the full value stays in title and the copy button.
const truncateMiddle = (id: string) => (id.length > 9 ? `${id.slice(0, 4)}…${id.slice(-4)}` : id)

// A subsection of OverviewTab's "API access" card, so it renders no card of its own.
export function MachineUsersSection({
  slug,
  state,
}: {
  slug: string
  state: ReturnType<typeof useMachineUsers>
}) {
  const [createOpen, setCreateOpen] = useState(false)
  const [revoking, setRevoking] = useState<MachineUser | null>(null)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-manrope text-sm font-semibold text-ideon-gray-black dark:text-ideon-gray-30">
          Machine users
        </h3>
        <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
          Create machine user
        </Button>
      </div>

      {/* A failure renders as a failure. An empty state here would read as "this
          account has no keys", which is a wrong answer rather than an incomplete
          one. The message is already operator-readable — the hook substitutes the
          403 text at the fetcher, where the status is available — so it renders
          bare rather than behind a "Failed to load:" prefix. */}
      {state.status === 'error' && <Alert variant="error">{state.message}</Alert>}

      {state.status !== 'error' && (
        <>
          {state.status === 'ready' && state.data.length === 0 ? (
            <EmptyState
              icon={<KeyRound className="h-10 w-10" />}
              heading="No machine users yet"
              // No action: the header's "Create machine user" button is always
              // present, and a second control with the same accessible name makes
              // a screen reader announce the action twice.
              description="A machine user is an M2M credential this account can use to call Ideon APIs."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Permission set</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Auth0 client ID</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              {state.status === 'loading' ? (
                <TableSkeleton columns={6} rows={3} />
              ) : (
                <TableBody>
                  {state.data.map((machineUser) => (
                    <TableRow key={machineUser.id}>
                      <TableCell className="font-medium">{machineUser.name}</TableCell>
                      <TableCell>{machineUser.permissionSetKey ?? '—'}</TableCell>
                      <TableCell>{machineUser.role}</TableCell>
                      <TableCell>
                        {machineUser.auth0ClientId ? (
                          <span className="flex items-center gap-2">
                            <code className="font-mono text-xs" title={machineUser.auth0ClientId}>
                              {truncateMiddle(machineUser.auth0ClientId)}
                            </code>
                            <CopyButton
                              value={machineUser.auth0ClientId}
                              label={`Copy client id for ${machineUser.name}`}
                              iconOnly
                            />
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>
                        <FormattedDate value={machineUser.createdAt} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="text"
                          className="text-ideon-state-error-dark dark:text-ideon-state-error"
                          onClick={() => setRevoking(machineUser)}
                          aria-label={`Revoke ${machineUser.name}`}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          )}
        </>
      )}

      <CreateMachineUserDialog
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        slug={slug}
        onCreated={state.reload}
      />
      {revoking && (
        <RevokeMachineUserDialog
          isOpen
          onClose={() => setRevoking(null)}
          slug={slug}
          machineUser={revoking}
          onRevoked={state.reload}
        />
      )}
    </div>
  )
}
